import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import CanvasClock from './CanvasClock';
import { ref, push } from "firebase/database";
import { database } from '../utils/firebase';

/* ── Sparkle colour palette ─────────────────────────────────── */
const SPARKLE_COLORS = [
    '#ff0033', // red
    '#ff1493', // deep pink
    '#ff6b9d', // hot pink
    '#c084fc', // violet
    '#fbbf24', // gold
    '#34d399', // emerald
    '#fb923c', // orange
    '#f472b6', // rose
    '#a78bfa', // purple
    '#38bdf8', // sky blue
    '#00ff00', // green
];

/* ── Helper: draw a 4-pointed star ───────────────────────────── */
const drawStar = (ctx, cx, cy, outerR, innerR, points = 5) => {
    ctx.beginPath();
    for (let i = 0; i < points * 2; i++) {
        const r = i % 2 === 0 ? outerR : innerR;
        const angle = (Math.PI / points) * i - Math.PI / 2;
        const x = cx + r * Math.cos(angle);
        const y = cy + r * Math.sin(angle);
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
    }
    ctx.closePath();
};

/* ── SparkleEffect component ─────────────────────────────────── */
const SparkleEffect = ({ containerRef }) => {
    const canvasRef = useRef(null);
    const particlesRef = useRef([]);
    const animFrameRef = useRef(null);
    const isHoveringRef = useRef(false);
    const lastSpawnTimeRef = useRef(0);
    const startLoopRef = useRef(null);
    const MAX_PARTICLES = 300;

    // Spawn a burst of sparkles at (x, y) relative to the canvas
    const spawnSparkles = useCallback((x, y) => {
        const now = Date.now();
        // Throttle spawn rate (only once every 10ms)
        if (now - lastSpawnTimeRef.current < 10) return;
        lastSpawnTimeRef.current = now;

        if (particlesRef.current.length > MAX_PARTICLES) return;

        const count = 3 + Math.floor(Math.random() * 4); // 3-6 sparkles per move event
        for (let i = 0; i < count; i++) {
            const angle = Math.random() * Math.PI * 2;
            const speed = 0.5 + Math.random() * 2.5;
            particlesRef.current.push({
                x,
                y,
                vx: Math.cos(angle) * speed,
                vy: Math.sin(angle) * speed,
                size: 2 + Math.random() * 5,
                color: SPARKLE_COLORS[Math.floor(Math.random() * SPARKLE_COLORS.length)],
                alpha: 1,
                decay: 0.005 + Math.random() * 0.01,
                rotation: Math.random() * Math.PI * 2,
                rotationSpeed: (Math.random() - 0.5) * 0.15,
            });
        }
        // Kick the animation loop if it's idle
        if (startLoopRef.current) startLoopRef.current();
    }, []);

    // Main animation loop — only runs when there are particles
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');

        const resize = () => {
            const container = containerRef.current;
            if (!container) return;
            canvas.width = container.offsetWidth;
            canvas.height = container.offsetHeight;
        };
        resize();
        window.addEventListener('resize', resize);

        const loop = () => {
            const particles = particlesRef.current;

            if (particles.length === 0) {
                ctx.clearRect(0, 0, canvas.width, canvas.height);
                animFrameRef.current = null; // mark loop as stopped
                return;
            }

            ctx.clearRect(0, 0, canvas.width, canvas.height);

            for (let i = particles.length - 1; i >= 0; i--) {
                const p = particles[i];
                p.x += p.vx;
                p.y += p.vy;
                p.vy += 0.02; // tiny gravity
                p.alpha -= p.decay;
                p.size *= 0.995;
                p.rotation += p.rotationSpeed;

                if (p.alpha <= 0 || p.size < 0.3) {
                    particles.splice(i, 1);
                    continue;
                }

                ctx.save();
                ctx.globalAlpha = p.alpha;
                ctx.translate(p.x, p.y);
                ctx.rotate(p.rotation);

                // glow
                ctx.shadowBlur = 12;
                ctx.shadowColor = p.color;

                // star shape
                ctx.fillStyle = p.color;
                drawStar(ctx, 0, 0, p.size, p.size * 0.4);
                ctx.fill();

                ctx.restore();
            }

            animFrameRef.current = requestAnimationFrame(loop);
        };

        // Expose a way to kick-start the loop from spawnSparkles
        startLoopRef.current = () => {
            if (!animFrameRef.current) {
                animFrameRef.current = requestAnimationFrame(loop);
            }
        };

        return () => {
            if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
            window.removeEventListener('resize', resize);
        };
    }, [containerRef]);

    // Mouse event handlers hooked to the container
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const onMouseMove = (e) => {
            if (!isHoveringRef.current) return;
            const rect = container.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const y = e.clientY - rect.top;
            spawnSparkles(x, y);
        };

        const onMouseEnter = () => {
            isHoveringRef.current = true;
        };

        const onMouseLeave = () => {
            isHoveringRef.current = false;
        };

        container.addEventListener('mousemove', onMouseMove);
        container.addEventListener('mouseenter', onMouseEnter);
        container.addEventListener('mouseleave', onMouseLeave);

        return () => {
            container.removeEventListener('mousemove', onMouseMove);
            container.removeEventListener('mouseenter', onMouseEnter);
            container.removeEventListener('mouseleave', onMouseLeave);
        };
    }, [containerRef, spawnSparkles]);

    return (
        <canvas
            ref={canvasRef}
            style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
                zIndex: 50,
            }}
        />
    );
};

/* ── Scroll-to-top button ────────────────────────────────────── */
const ScrollToTopButton = () => (
    <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="fixed bottom-16 sm:bottom-28 right-8 sm:right-12 z-50 flex items-center justify-center rounded-full p-2 w-12 h-12 shadow-lg transition-colors"
        aria-label="Scroll to top"
        style={{
            color: ' #61dafb',
            backgroundColor: 'black',
            cursor: 'pointer'
        }}
        onMouseEnter={e => {
            e.currentTarget.style.backgroundColor = ' #61dafb';
            e.currentTarget.style.color = '#0a0d37';
        }}
        onMouseLeave={e => {
            e.currentTarget.style.backgroundColor = 'black';
            e.currentTarget.style.color = '#61dafb';
        }}
    >
        {/* Upward arrow SVG with animation */}
        <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6 animate-bounce"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
        >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={4} d="M5 18l7-7 7 7" />
        </svg>
    </button>
);

/* ── Contact page ────────────────────────────────────────────── */
const Contact = () => {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [message, setMessage] = useState('');
    const [isSending, setIsSending] = useState(false);
    const [sendResult, setSendResult] = useState(null);
    const mainSectionRef = useRef(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSending(true);
        setSendResult(null);

        try {
            if (name && email && message) {
                const messageData = {
                    name: name,
                    email: email,
                    message: message
                };
                const messagesRef = ref(database, 'messages');
                await push(messagesRef, messageData);
                setSendResult({ success: true, message: 'Message sent successfully!' });
                setName('');
                setEmail('');
                setMessage('');
            }
            else {
                setSendResult({ success: false, message: 'Please fill all the details.' });
            }
        } catch (error) {
            console.error("Error submitting form:", error);
            setSendResult({ success: false, message: `Error sending message: ${error.message}` });
        } finally {
            setIsSending(false);
        }
    };

    // Email validation regex
    const isValidEmail = (email) => {
        // Simple RFC 5322 compliant regex
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    };

    return (
        <div className="w-full h-full min-h-screen bg-black relative mt-1 pb-4 lg:pb-2" ref={mainSectionRef}>
            {/* ✨ Sparkle canvas overlay */}
            <SparkleEffect containerRef={mainSectionRef} />

            <div className="relative px-2 py-1 mx-4 mt-4 mb-0 md:mx-0 font-bold pt-[4%] text-3xl">
                <img className="w-full h-screen absolute object-cover opacity-20" src="assets/contact-bg-full.jpg" alt="contact-bg"></img>
                <div className="relative flex items-center justify-center">
                    <h1 className="text-white text-4xl items-center justify-center sm:text-4xl md:text-5xl lg:text-6xl pt-14 pb-0 sm:pt-8 sm:pb-1 pl-2 font-bold text-center">
                        Contact
                    </h1>
                </div>
            </div>
            <div className="relative flex flex-col lg:flex-row mt-0 lg:backdrop-filter backdrop-blur-2xl">
                <div className="w-full lg:w-1/2 backdrop-filter backdrop-blur-2xl mt-[-40px]">
                    <form className="mt-1 mx-3 sm:mx-4 px-4 sm:px-12 py-8 flex flex-col gap-4 text-lg sm:text-2xl backdrop-filter backdrop-blur-lg">
                        <label className="flex flex-col py-2 pt-8">
                            <span className="text-white font-bold mb-2 sm:mb-4">Name</span>
                            <input
                                type="text"
                                name="name"
                                placeholder="What is your name?"
                                className="py-3 sm:py-4 px-4 sm:px-6 bg-gray-600 font-semibold rounded-lg outline-none border-none"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                            />
                        </label>
                        <label className="flex flex-col">
                            <span className="text-white font-bold mb-2 sm:mb-4">Email</span>
                            <input
                                type="email"
                                name="email"
                                placeholder="What is your email address?"
                                className="py-3 sm:py-4 px-4 sm:px-6 bg-gray-600 font-semibold rounded-lg outline-none border-none"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                            />
                        </label>
                        <label className="flex flex-col">
                            <span className="text-white font-bold mb-2 sm:mb-4">Message</span>
                            <textarea
                                rows="2"
                                name="message"
                                placeholder="What do you want to say?"
                                className="py-3 sm:py-4 px-4 sm:px-6 bg-gray-600 font-semibold rounded-lg outline-none border-none"
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                            >
                            </textarea>
                        </label>
                        <div className="flex relative">
                            <div>
                                <button
                                    type="submit"
                                    className="w-full sm:w-28 md:w-32 lg:w-32 xl:w-32 rounded-xl border-solid border mt-4 border-[#61dafb] py-2 px-6 font-semibold"
                                    style={{
                                        color: ' #61dafb',
                                        backgroundColor: 'black',
                                        cursor: 'pointer'
                                    }}
                                    onMouseEnter={e => {
                                        e.currentTarget.style.backgroundColor = ' #61dafb';
                                        e.currentTarget.style.color = '#0a0d37';
                                    }}
                                    onMouseLeave={e => {
                                        e.currentTarget.style.backgroundColor = 'black';
                                        e.currentTarget.style.color = '#61dafb';
                                    }}
                                    disabled={isSending}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setSendResult(null);
                                        if (!name || !email || !message) {
                                            setSendResult({ success: false, message: 'Please fill all the details.' });
                                            return;
                                        }
                                        if (!isValidEmail(email)) {
                                            setSendResult({ success: false, message: 'Please enter a valid email address.' });
                                            return;
                                        }
                                        handleSubmit(e);
                                    }}
                                >
                                    {isSending ? 'Sending...' : 'Send'}
                                </button>
                            </div>
                            <div className="px-4 py-7 text-white-100 text-base sm:text-lg md:text-xl">
                                {sendResult && (
                                    <p className={sendResult.success ? 'text-green-500 font-semibold' : 'text-red-500 font-semibold'}>
                                        {sendResult.message}
                                    </p>
                                )}
                            </div>
                        </div>
                    </form>
                </div>
                <div className="w-full lg:w-1/2 flex flex-col justify-center items-center mt-6 lg:mt-[-40px] px-4 lg:pl-6">
                    <CanvasClock />
                </div>
                <ScrollToTopButton />
            </div>
            <div className="relative z-20 flex justify-center lg:justify-end pt-10 pb-14 lg:pt-0 lg:pb-0">
                <h1 className="relative -mt-10 lg:-mt-16 mx-4 lg:mr-12 text-center lg:text-right text-white">
                    © 2026 Designed and Developed by <Link className="text-[#ff0080] hover:text-[#7928ca]" to="https://in.linkedin.com/in/krishnendu-addya-025915121" target="_blank" rel="noreferrer">Krishnendu Addya</Link>
                </h1>
            </div>
        </div>
    );
};

export default Contact;
